import { salesChecklistData, salesChecklistMeta } from '../data/checklists/salesChecklist';
import {
  operationsChecklistData,
  operationsChecklistMeta,
  operationsPillarLabels,
} from '../data/checklists/operationsChecklist';

import { LEGAL_LINKS } from '../data/legalLinks';

const POLICY_LINK = LEGAL_LINKS.privacy;

export const SALES_CHECKLIST_PAGE = {
  id: 'sales',
  checklistData: salesChecklistData,
  meta: salesChecklistMeta,
  source: 'ready-for-ai-sales',
  scale: {
    type: 'numeric',
    values: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    labels: null,
    answerSuffix: '/10',
  },
  seo: {
    title: 'AI для отдела продаж - чек-лист | Антология',
    description:
      'Чек-лист готовности отдела продаж к Generative AI: CRM, КП, контент. Подбор AI-решений для продаж.',
    canonical: 'https://antologia.tech/ready-for-ai-sales',
  },
  hero: {
    title: 'Готовы ли вы к Generative AI в продажах?',
    titleHighlight: 'Generative AI',
    subtitle:
      'Оцените процессы, команду, инструменты и контент отдела продаж. Около 8-10 минут, в конце - сводка баллов.',
  },
  showIcpFields: true,
  icpProfile: 'sales',
  icpFields: {
    roles: [
      { value: 'commercial_director', label: 'Коммерческий директор' },
      { value: 'rom', label: 'РОМ' },
      { value: 'rop', label: 'РОП' },
      { value: 'sales_manager', label: 'Менеджер по продажам' },
    ],
    sizes: [
      { value: 'under100', label: 'Менее 100 сотрудников' },
      { value: '100-500', label: '100-500 сотрудников' },
      { value: '500plus', label: 'Более 500 сотрудников' },
    ],
    industries: [
      { value: 'trade', label: 'Торговля' },
      { value: 'it', label: 'IT' },
      { value: 'other', label: 'Другая отрасль' },
    ],
    triggers: [
      { value: 'sales_load', label: 'Рост плана / нагрузка на отдел продаж' },
      { value: 'manual_content', label: 'Много ручных КП, писем и материалов' },
      { value: 'efficiency', label: 'Давление по конверсии и эффективности' },
      { value: 'crm', label: 'Хаос в CRM или качестве данных' },
      { value: 'other', label: 'Другое' },
    ],
  },
  result: {
    scoreLabel: (max) => `из ${max} баллов`,
    ctaTitle: 'Хотите внедрить Gen AI в продажи?',
    ctaText: 'Получите отчёт и запишитесь на консультацию с экспертом.',
    ctaHeading: 'Хотите внедрить Gen AI в продажи?',
    ctaSubtext: 'Получите результаты и запишитесь на консультацию с экспертом',
    ctaLabel: 'Получить полные результаты и записаться',
    showPillars: false,
    blogLink: null,
    summaries: {
      0: 'Сейчас отделу продаж нужна базовая подготовка: процессы, инструменты и контент требуют выстраивания перед внедрением Gen AI.',
      101: 'У вас уже есть задел, но несколько направлений всё ещё требуют доработки перед масштабированием AI в продажах.',
      201: 'Отдел продаж близок к пилоту: можно тестировать Gen AI на ограниченном контуре и готовить масштабирование.',
    },
  },
  reportTitle: 'Чек-лист «Готовность отдела продаж к Generative AI»',
  readinessBands: [
    { min: 201, label: 'Можно внедрять', color: 'text-[#7CB342]', emoji: '✅' },
    { min: 101, label: 'Частично готовы', color: 'text-yellow-600', emoji: '⚠️' },
    { min: 0, label: 'Не готовы', color: 'text-red-600', emoji: '❌' },
  ],
  readinessScaleHelp: `0-100   Не готовы (нужна базовая подготовка)
101-200 Частично готовы (работайте над процессами)
201-230 Можно внедрять (готовы к тестированию)`,
  policyLink: POLICY_LINK,
};

export const OPERATIONS_CHECKLIST_PAGE = {
  id: 'operations',
  checklistData: operationsChecklistData,
  meta: operationsChecklistMeta,
  pillarLabels: operationsPillarLabels,
  source: 'ready-for-ai',
  scale: {
    type: 'likert',
    values: [0, 1, 2],
    labels: ['Нет', 'Частично', 'Да'],
    answerSuffix: '',
  },
  seo: {
    title: 'Внедрение ИИ в бизнес - чек-лист готовности | Антология',
    description:
      'Оцените готовность компании к внедрению корпоративного ИИ. Чек-лист для COO и операционных директоров, консультация эксперта.',
    canonical: 'https://antologia.tech/ready-for-ai',
  },
  hero: {
    badge: null,
    title: 'Готовность компании к пилоту ИИ',
    titleHighlight: 'пилоту ИИ',
    subtitle:
      'Для COO, операционных директоров и руководителей цифровой трансформации. Около 7 минут - задача пилота, три опоры готовности и рекомендация уровня внедрения.',
  },
  showIcpFields: true,
  icpProfile: 'operations',
  icpFields: {
    roles: [
      { value: 'coo', label: 'COO / операционный директор' },
      { value: 'ceo', label: 'CEO / генеральный директор' },
      { value: 'cio', label: 'CIO / директор по ИТ' },
      { value: 'other', label: 'Другая роль' },
    ],
    sizes: [
      { value: 'under100', label: 'Менее 100 сотрудников' },
      { value: '100-500', label: '100-500 сотрудников' },
      { value: '500plus', label: 'Более 500 сотрудников' },
    ],
    industries: [
      { value: 'trade', label: 'Торговля' },
      { value: 'it', label: 'IT' },
      { value: 'other', label: 'Другая отрасль' },
    ],
    triggers: [
      { value: 'ops_load', label: 'Рост нагрузки на операции' },
      { value: 'efficiency', label: 'Давление по эффективности' },
      { value: 'compliance', label: 'Аудит / регуляторика' },
      { value: 'other', label: 'Другое' },
    ],
  },
  result: {
    scoreLabel: (max) => `из ${max} (шкала: нет / частично / да)`,
    ctaTitle: 'Разобрать результат с менеджером',
    ctaText:
      'Получите отчёт на почту и обсудите рамки пилота и расчёт внедрения — без обещания фиксированного ROI до погружения в ваш контур.',
    ctaHeading: 'Разобрать результат с менеджером',
    ctaSubtext:
      'Получите отчёт на почту и обсудите рамки пилота и расчёт внедрения — без обещания фиксированного ROI до погружения в ваш контур.',
    ctaLabel: 'Получить полные результаты и записаться',
    showPillars: true,
    showPilotRecommendation: true,
    blogLink: {
      path: '/blog/klassifikaciya-ii-reshenij-chat-rag-agent-harness?utm_source=checklist&utm_medium=organic&utm_campaign=ops_ai_classification',
      label: 'Подробнее: классификация ИИ-решений',
    },
    summaries: {
      0: 'Сначала нужна подготовка: данные, владелец процесса и интеграции требуют проработки до запуска пилота ИИ.',
      13: 'К пилоту можно приступать с ограничениями: сузьте контур и зафиксируйте метрики на 4–8 недель.',
      25: 'Опоры согласованы — можно планировать пилот ИИ с понятным владельцем и первым рабочим контуром.',
    },
  },
  reportTitle: 'Чек-лист «Готовность компании к пилоту ИИ»',
  readinessBands: [
    { min: 25, label: 'Можно планировать пилот', color: 'text-green-600', emoji: '✅' },
    { min: 13, label: 'Пилот с ограничениями', color: 'text-yellow-600', emoji: '⚠️' },
    { min: 0, label: 'Сначала подготовка', color: 'text-red-600', emoji: '❌' },
  ],
  readinessScaleHelp: `0-12   Сначала подготовка (данные, владелец, интеграции)
13-24  Пилот с ограничениями (узкий контур, жёсткие рамки)
25-36  Можно планировать пилот (согласованы опоры)`,
  policyLink: POLICY_LINK,
};
